/**
 * Various untilies to encode, manipulate and convert between values of various units.
 */

function normalizeUnitValue(value, Klass) {
  if (value instanceof Klass) {
    return value.valueOf();
  }

  const normalized = typeof value === 'string' ? Number(value) : value;
  if (typeof normalized !== 'number' || !Number.isFinite(normalized)) {
    throw new Error(`'${value}' is not a valid value`);
  }

  return normalized;
}

class Distance {
  static get units() {
    return ['mi', 'km'];
  }

  constructor(value, unit = 'mi') {
    this.value = normalizeUnitValue(value, Distance);
    this.unit = unit;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return `${Math.round(this.value * 10) / 10}${this.unit}`;
  }

  asRounded(unit) {
    return Math.round(this.as(unit) * 10) / 10;
  }

  as(unit) {
    if (!Distance.units.includes(unit)) {
      throw new Error(`'${unit}' is not a valid unit`);
    }
    return Distance.convert(this.value, this.unit, unit).valueOf();
  }

  into(unit) {
    return new Distance(this.as(unit), unit);
  }

  static convert(value, from, to) {
    const normalizedValue = normalizeUnitValue(value, Distance);
    if (!Distance.units.includes(from)) {
      throw new Error(`'${from}' is not a valid unit`);
    }
    if (!Distance.units.includes(to)) {
      throw new Error(`'${to}' is not a valid unit`);
    }

    if (from === to) {
      return normalizedValue;
    }

    const MI_OVER_KM = 1.609344;

    switch (`${from} -> ${to}`) {
      case 'mi -> km':
        return normalizedValue * MI_OVER_KM;

      case 'km -> mi':
        return normalizedValue / MI_OVER_KM;

      default:
        throw new Error('Unreachable');
    }
  }
}

class Temperature {
  static get units() {
    return ['c', 'f'];
  }

  constructor(value, unit = 'f') {
    this.value = normalizeUnitValue(value, Temperature);
    this.unit = unit;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return `${Math.round(this.value * 10) / 10} °${this.unit.toUpperCase()}`;
  }

  asRounded(unit) {
    return Math.round(this.as(unit) * 10) / 10;
  }

  as(unit) {
    if (!Temperature.units.includes(unit)) {
      throw new Error(`'${unit}' is not a valid unit`);
    }
    return Temperature.convert(this.value, this.unit, unit).valueOf();
  }

  into(unit) {
    return new Temperature(this.as(unit), unit);
  }

  static convert(value, from, to) {
    const normalizedValue = normalizeUnitValue(value, Temperature);
    if (!Temperature.units.includes(from)) {
      throw new Error(`'${from}' is not a valid unit`);
    }
    if (!Temperature.units.includes(to)) {
      throw new Error(`'${to}' is not a valid unit`);
    }

    if (from === to) {
      return normalizedValue;
    }

    switch (`${from} -> ${to}`) {
      case 'f -> c':
        return (normalizedValue - 32) * (5 / 9);

      case 'c -> f':
        return normalizedValue * (9 / 5) + 32;

      default:
        throw new Error('Unreachable');
    }
  }
}

class Weight {
  static get units() {
    return ['lbs', 'kgs', 'meals'];
  }

  constructor(value, unit = 'lbs') {
    this.value = normalizeUnitValue(value, Weight);
    this.unit = unit;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return `${Math.round(this.value * 10) / 10} ${this.unit}`;
  }

  as(unit) {
    if (!Weight.units.includes(unit)) {
      throw new Error(`'${unit}' is not a valid unit`);
    }
    return Weight.convert(this.value, this.unit, unit).valueOf();
  }

  asRounded(unit) {
    return Math.round(this.as(unit) * 10) / 10;
  }

  into(unit) {
    return new Weight(this.as(unit), unit);
  }

  static convert(value, from, to) {
    const normalizedValue = normalizeUnitValue(value, Weight);
    if (!Weight.units.includes(from)) {
      throw new Error(`'${from}' is not a valid unit`);
    }
    if (!Weight.units.includes(to)) {
      throw new Error(`'${to}' is not a valid unit`);
    }

    if (from === to) {
      return normalizedValue;
    }

    const KGS_OVER_LBS = 0.45359237; // kg   = lb * KGS_OVER_LBS
    const MEALS_OVER_LBS = 1.2; // meal = lb * MEALS_OVER_LBS

    switch (`${from} -> ${to}`) {
      case 'kgs -> lbs':
        return normalizedValue / KGS_OVER_LBS;

      case 'lbs -> kgs':
        return normalizedValue * KGS_OVER_LBS;

      case 'meals -> lbs':
        return normalizedValue * MEALS_OVER_LBS;

      case 'lbs -> meals':
        return normalizedValue / MEALS_OVER_LBS;

      case 'kgs -> meals':
        return normalizedValue / KGS_OVER_LBS / MEALS_OVER_LBS;

      case 'meals -> kgs':
        return normalizedValue * MEALS_OVER_LBS * KGS_OVER_LBS;

      default:
        throw new Error(`Unreachable ${from} -> ${to}`);
    }
  }
}

/**
 * Lowercase, drop periods and collapse whitespace so "U.S.A." matches "USA".
 */
function normalizeCountryName(country) {
  return String(country || '')
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Imported orgs can carry abbreviations ("USA") instead of Google's
 * "United States", so the common US spellings are listed too.
 */
const IMPERIAL_COUNTRIES = new Set(
  [
    'United States',
    'United States of America',
    'US',
    'USA',
    'Puerto Rico',
    'U.S. Virgin Islands',
    'Guam',
    'Palau',
    'Belize',
    'Bahamas',
    'Cayman Islands',
    'Liberia',
    'Myanmar',
  ].map(normalizeCountryName)
);

/**
 * Determine default unit based on team's country.
 * @params {string} user.account_type
 * @params {string | null} user.country Team's country
 * @returns boolean
 */
const determineDefaultUnit = (user) => {
  const { account_type, country, isCorporate } = user;

  return (
    account_type === 'ADMIN' ||
    IMPERIAL_COUNTRIES.has(normalizeCountryName(country)) ||
    isCorporate
  );
};

/**
 * Given the `currentUser`, return the units preferred by that user based on
 * their location.
 */
const getLocaleUnit = (currentUser) => {
  const imperial = determineDefaultUnit({
    account_type: currentUser.account_type,
    country: currentUser.country,
  });

  return {
    weight: imperial ? 'lbs' : 'kgs',
    temp: imperial ? 'f' : 'c',
    distance: imperial ? 'mi' : 'km',
  };
};

module.exports = {
  Distance,
  Temperature,
  Weight,
  getLocaleUnit,
  determineDefaultUnit,
};
